// components/InvoicePdfView.jsx
import React, { useRef } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export default function InvoicePdfView({ invoice, onClose }) {
  const invoiceRef = useRef(null);

  const handleDownloadPdf = async () => {
    if (!invoiceRef.current) return;

    try {
      const element = invoiceRef.current;

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${invoice.invoiceNumber || "invoice"}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Failed to generate PDF");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto p-6">
        {/* Header Buttons */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">
            Invoice Preview - {invoice.invoiceNumber}
          </h2>
          <div className="flex gap-2">
            <button
              onClick={handleDownloadPdf}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
            >
              Download PDF
            </button>
            <button
              onClick={onClose}
              className="px-3 py-2 rounded-lg text-sm font-semibold border border-gray-300 hover:bg-gray-100"
            >
              Close
            </button>
          </div>
        </div>

        {/* This DIV becomes the PDF */}
        <div
          ref={invoiceRef}
          className="bg-white w-full shadow border border-gray-200 rounded-xl p-8 text-sm"
        >
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">TAX INVOICE</h1>
              <p className="text-gray-600 mt-2 font-semibold">
                {invoice.firmName}
              </p>
              <p className="text-gray-500 text-xs mt-1">
                GSTIN: {invoice.gstin}
              </p>
            </div>
            <div className="text-right text-xs text-gray-600">
              <p>
                <span className="font-semibold">Invoice No:</span>{" "}
                {invoice.invoiceNumber}
              </p>
              <p>
                <span className="font-semibold">Invoice Date:</span>{" "}
                {invoice.invoiceDate &&
                  new Date(invoice.invoiceDate).toLocaleDateString("en-IN")}
              </p>
              <p>
                <span className="font-semibold">Due Date:</span>{" "}
                {invoice.dueDate &&
                  new Date(invoice.dueDate).toLocaleDateString("en-IN")}
              </p>
            </div>
          </div>

          {/* Bill To */}
          <div className="border border-gray-200 rounded-lg mb-6">
            <div className="bg-gray-100 px-4 py-2 text-xs font-semibold">
              Bill To
            </div>
            <div className="px-4 py-3 text-xs text-gray-700">
              <p>{invoice.firmName}</p>
              <p>Phone: {invoice.dealerPhone}</p>
              {invoice.gstin && <p>GSTIN: {invoice.gstin}</p>}
            </div>
          </div>

          {/* Items Table */}
          <table className="w-full text-xs border border-gray-300 mb-4 border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-2 py-2 text-left">
                  #
                </th>
                <th className="border border-gray-300 px-2 py-2 text-left">
                  Description
                </th>
                <th className="border border-gray-300 px-2 py-2 text-right">
                  HSN
                </th>
                <th className="border border-gray-300 px-2 py-2 text-right">
                  Qty
                </th>
                <th className="border border-gray-300 px-2 py-2 text-right">
                  Rate
                </th>
                <th className="border border-gray-300 px-2 py-2 text-right">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {invoice.items?.map((item, index) => (
                <tr key={index}>
                  <td className="border border-gray-300 px-2 py-1">
                    {index + 1}
                  </td>
                  <td className="border border-gray-300 px-2 py-1">
                    {item.description}
                  </td>
                  <td className="border border-gray-300 px-2 py-1 text-right">
                    {item.hsnCode}
                  </td>
                  <td className="border border-gray-300 px-2 py-1 text-right">
                    {item.quantity} {item.unit}
                  </td>
                  <td className="border border-gray-300 px-2 py-1 text-right">
                    ₹{Number(item.rate).toLocaleString("en-IN")}
                  </td>
                  <td className="border border-gray-300 px-2 py-1 text-right">
                    ₹{Number(item.amount).toLocaleString("en-IN")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals and Tax */}
          <div className="flex justify-between gap-4 mb-4">
            <div className="w-1/2 text-xs text-gray-600">
              <p className="font-semibold mb-1">Amount in Words:</p>
              <p>{invoice.amountInWords}</p>
            </div>
            <div className="w-1/2">
              <table className="w-full text-xs border border-gray-300 border-collapse">
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-2 py-1">
                      Subtotal
                    </td>
                    <td className="border border-gray-300 px-2 py-1 text-right">
                      ₹{Number(invoice.subtotal || 0).toLocaleString("en-IN")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-2 py-1">CGST</td>
                    <td className="border border-gray-300 px-2 py-1 text-right">
                      ₹{Number(invoice.cgst || 0).toLocaleString("en-IN")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-2 py-1">SGST</td>
                    <td className="border border-gray-300 px-2 py-1 text-right">
                      ₹{Number(invoice.sgst || 0).toLocaleString("en-IN")}
                    </td>
                  </tr>
                  <tr className="bg-gray-100 font-semibold">
                    <td className="border border-gray-300 px-2 py-1">
                      Total Amount
                    </td>
                    <td className="border border-gray-300 px-2 py-1 text-right">
                      ₹{Number(invoice.totalAmount || 0).toLocaleString(
                        "en-IN"
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between mt-8 text-xs">
            <div className="text-gray-500">
              <p>Request ID: {invoice.requestId}</p>
              <p className="mt-1">
                Payment Status:{" "}
                <span className="font-semibold">
                  {invoice.paymentStatus || "N/A"}
                </span>
              </p>
            </div>
            <div className="text-right">
              <p className="font-semibold">For {invoice.firmName}</p>
              <div className="h-10" />
              <p className="text-gray-600 text-xs">Authorised Signatory</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
